import { create } from 'apisauce';
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';
import { endSession, getAuthToken } from '../lib/sessionStore';

/**
 * ── The base HTTP client ────────────────────────────────────────────────
 *
 * One apisauce instance for the whole app. `BASE_URL` already includes the
 * `/api` prefix (and no `/v1` — the ozone API doesn't version its path), so an
 * endpoint here is written exactly as the bruno collection writes it:
 * `/auth/login`, `/me`, `/notifications/topics/`.
 *
 * ── The token, and why it's read from storage per request ───────────────
 * This module does not OWN the token — [[sessionStore]] does, along with the
 * user, so that ending a session is one call and not two things to remember. All
 * that happens here is a read per request.
 *
 * A keychain read per call is the cost, and the thing it buys is that there's
 * exactly one place the token lives: a token written by the auth flow is picked
 * up by the very next request with no hydration step and no cache to go stale.
 * Revisit only if a profiler says to.
 *
 * The header goes on EVERY request, including the anonymous auth steps
 * (`login` / `register` / mobile `verify`). The server ignores it there — those
 * actions are `AllowAny` — so it's harmless, and the alternative (an opt-in auth
 * flag per call) is bookkeeping at every call site to save a header the server
 * throws away.
 *
 * ── snake_case ⇄ camelCase happens HERE, once ────────────────────────────
 * The ozone API is snake_case end to end and the app is camelCase. The two
 * transforms below convert both directions for every call, so NO module above
 * this one writes a mapping for casing alone: an endpoint's types are declared in
 * camelCase and read as camelCase, and request bodies are written in camelCase.
 *
 * Only KEYS are touched, never values — `event_kind: 'secret-menu'` arrives as
 * `eventKind: 'secret-menu'`, kebab intact — and the snake direction is
 * idempotent, so a body still written `{ challenge_id }` goes out unharmed.
 *
 * Two consequences worth knowing:
 *
 *   - The types are now a CLAIM, not a check. Nothing verifies that the
 *     camelCase interface you declared is what the transform produces from the
 *     wire; a mistyped field is still a runtime undefined. Mapping by hand had
 *     the same property, so this trades no safety away — but don't read
 *     `api.get<Foo>` as validation.
 *   - A key that is DATA gets renamed too. The transform can't tell a field name
 *     from a server-defined map key, so an endpoint returning `{[topic]: bool}`
 *     needs `stopPaths` on the response transform — see `DATA_KEY_PATHS` below,
 *     which exempts a membership's perk map. (`/notifications/topics/` is the
 *     other such endpoint and is being rewritten.)
 * ───────────────────────────────────────────────────────────────────────
 */

// The base URL comes from the environment, with NO fallback on purpose. A
// default would have to be production, and a dev build whose env didn't load
// would then quietly mint real accounts and send real SMS against it — a
// mistake you'd discover from the other side. Failing at import is loud, it
// happens immediately, and it names the fix.
//
// EXPO_PUBLIC_* vars are inlined by Babel at BUILD time, so this must be a
// direct `process.env.X` reference — destructuring it or building the key
// dynamically reads an empty object at runtime.
const apiBaseUrl = process.env.EXPO_PUBLIC_API_BASE_URL;

if (!apiBaseUrl) {
  throw new Error(
    'EXPO_PUBLIC_API_BASE_URL is not set. Copy .env.example to .env (see that file for the ozone URLs), then restart the bundler — env vars are inlined at build time, so a running bundler will not pick it up.'
  );
}

const api = create({
  baseURL: apiBaseUrl,
  // apisauce defaults to axios' `timeout: 0` — never aborts. A half-open socket
  // would leave a request pending forever, wedging any caller that guards on
  // "already in flight" (useAllExperiences.loadMore). Classifies as `network`.
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept-Encoding': 'gzip',
  },
});

api.addAsyncRequestTransform(async (request) => {
  const token = await getAuthToken();
  if (token) {
    request.headers = request.headers ?? {};
    request.headers['Authorization'] = `Token ${token}`;
  }
});

// `snakecaseKeys` THROWS on anything that isn't a plain object (or an array of
// them), so this guard is load-bearing rather than defensive: a string or
// FormData body would otherwise take the request down before it was sent.
const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && value.constructor === Object;

// Nodes whose CHILD KEYS are data rather than field names. The whole subtree
// under one of these is left exactly as sent — including the leaf objects' own
// snake_case fields, which the module that reads them maps by hand.
//
// `perks_usage.perks` is keyed by event kind (`ps_original`, `secret_menu`) —
// the same strings an event's `eventKind` VALUE carries, so camelCasing them
// would quietly make `perks[event.eventKind]` miss. Paths are matched whole, so
// every nesting a membership block arrives at needs its own entry.
// An array contributes nothing to the path — its ELEMENTS are walked under the
// array's own key — so a list envelope's rows sit at `data.…`, not `data.0.…`.
const DATA_KEY_PATHS = [
  'perks_usage.perks', // GET /me/memberships/{id}
  'data.perks_usage.perks', // GET /me/memberships — a row of the list envelope
  'membership.perks_usage.perks', // GET /me, POST /auth/verify, POST /membership
  'past_memberships.perks_usage.perks', // GET /me, once it sends them
];

// Responses: snake_case → camelCase, in place, at every depth.
//
// Only successful bodies. A failure's body is the `{"error": {…}}` envelope,
// whose keys are all single words anyway — and its `field` names a form field
// using the SERVER's spelling (`first_name`), which apiError passes through
// untouched. Leaving errors exactly as sent keeps that mapping the server's.
api.addResponseTransform((response) => {
  if (response.ok && response.data) {
    response.data = camelcaseKeys(response.data, {
      deep: true,
      stopPaths: DATA_KEY_PATHS,
    });
  }
});

// Requests: camelCase → snake_case, for bodies and for query params.
api.addRequestTransform((request) => {
  if (isPlainObject(request.data)) {
    request.data = snakecaseKeys(request.data, { deep: true });
  }
  if (isPlainObject(request.params)) {
    request.params = snakecaseKeys(request.params, { deep: true });
  }
});

/**
 * ── The 401 handler: one place the session dies ─────────────────────────
 *
 * A 401 on an authenticated call is the server stating the credential is dead —
 * never issued, or soft-revoked by `logout`. No retry changes that, so the token
 * and the in-memory user both go, here, rather than at each call site. This is
 * the ONLY status that does it: a timeout or a 5xx is evidence about the moment,
 * not about the token, and signing someone out for opening the app in a tunnel
 * is a bug that looks exactly like a security feature.
 *
 * ── Why /auth/ is exempt ────────────────────────────────────────────────
 * The `Authorization` header goes on EVERY request, including the anonymous auth
 * steps (see the transform above). So a 401 from `/auth/login` or `/auth/verify`
 * reaches this handler too — and there it means "that OTP was wrong", not "your
 * token is dead".
 *
 * Today those return 400 (`wrong_arguments`), so this exemption changes no
 * observed behaviour. It's here because the failure it prevents is severe and
 * silent: a user three steps into signup mistypes a code, and the app destroys
 * the account they just created — mid-flow, with the next screen's `PATCH /me`
 * failing for a reason nothing on screen explains. That's one backend status
 * change away, and it would be diagnosed as anything but this.
 *
 * `substring`, not `startsWith`: every call site here writes a relative path, so
 * `config.url` is `/auth/login` — but if one ever passes an absolute URL, a
 * prefix test fails OPEN, straight into the case above. This fails closed.
 *
 * ── Async, on purpose ───────────────────────────────────────────────────
 * An async transform is awaited before the caller's promise settles, so the
 * token is gone by the time a screen sees the error. A sync one would let a
 * retry race the delete and re-send the credential we just decided was dead.
 *
 * Callers still get their rejection — this doesn't swallow the failure, it only
 * makes sure the session doesn't outlive it. Screens react by reading the store:
 * `<Stack.Protected>` in app/_layout.tsx pops anyone off a signed-in route.
 */
api.addAsyncResponseTransform(async (response) => {
  if (response.status !== 401) return;
  if (response.config?.url?.includes('/auth/')) return;

  await endSession();
});

// // ── TEMP: force every response to look like a 5xx, to exercise the toast path.
// // DELETE THIS BLOCK.
// api.addResponseTransform((response) => {
//   response.ok = false;
//   response.status = 500;
//   response.problem = 'SERVER_ERROR';
//   response.data = undefined;
// });

export default api;
